import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { adminApi } from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { Star, LogOut, Loader2, ArrowLeft, Building2, Mail, RefreshCw, AlertTriangle, Plus, UserPlus } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { de } from 'date-fns/locale';

export default function AdminTenantDetail() {
  const { tenantId } = useParams();
  const { user, loading: authLoading, logout } = useAuth('admin_user');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '' });
  const [creatingUser, setCreatingUser] = useState(false);

  useEffect(() => {
    if (user) loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, tenantId]);

  const loadData = async () => {
    try {
      const res = await adminApi.getTenant(tenantId);
      setData(res.data);
    } catch (err) {
      toast.error('Fehler beim Laden');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (status) => {
    setUpdating(true);
    try {
      await adminApi.updateTenant(tenantId, { status });
      toast.success('Status aktualisiert');
      loadData();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setUpdating(false);
    }
  };

  const handlePaymentChange = async (payment_status) => {
    setUpdating(true);
    try {
      await adminApi.updateTenant(tenantId, {
        billing: { ...data.tenant.billing, payment_status }
      });
      toast.success('Payment-Status aktualisiert');
      loadData();
    } catch (err) {
      toast.error('Fehler beim Aktualisieren');
    } finally {
      setUpdating(false);
    }
  };

  const handlePlanChange = async (plan) => {
    setUpdating(true);
    try {
      await adminApi.updateTenant(tenantId, {
        billing: { ...data.tenant.billing, plan }
      });
      toast.success(`Plan auf "${plan}" gesetzt`);
      loadData();
    } catch (err) {
      toast.error('Fehler beim Plan-Wechsel');
    } finally {
      setUpdating(false);
    }
  };

  const handleResetLogin = async () => {
    try {
      const res = await adminApi.resetTenantLogin(tenantId);
      toast.success('Login-Link wurde erstellt');
      if (res.data.dev_token) {
        const url = `${window.location.origin}/app/auth/verify?token=${res.data.dev_token}`;
        navigator.clipboard.writeText(url);
        toast.info('Link wurde in Zwischenablage kopiert');
      }
    } catch (err) {
      toast.error('Fehler beim Reset');
    }
  };

  const handleCreateUser = async () => {
    if (!newUser.email) return;
    setCreatingUser(true);
    try {
      const res = await adminApi.createHVUser(tenantId, newUser);
      toast.success('HV User erstellt!');
      if (res.data.dev_token) {
        const url = `${window.location.origin}/app/auth/verify?token=${res.data.dev_token}`;
        navigator.clipboard.writeText(url);
        toast.info('Login-Link wurde in Zwischenablage kopiert');
      }
      setCreateUserOpen(false);
      setNewUser({ email: '', name: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Fehler beim Erstellen');
    } finally {
      setCreatingUser(false);
    }
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!data?.tenant) {
    return (
      <div className="min-h-screen bg-muted/30 flex items-center justify-center">
        <Card className="max-w-md">
          <CardContent className="pt-6 text-center">
            <AlertTriangle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h1 className="text-xl font-semibold mb-2">Tenant nicht gefunden</h1>
            <Link to="/admin/tenants">
              <Button variant="secondary">Zurück</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { tenant, hv_user, review_count, link_count, meta_scores } = data;

  return (
    <div className="min-h-screen bg-muted/30">
      {/* Header */}
      <header className="bg-primary text-primary-foreground border-b">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="flex items-center gap-2">
              <div className="w-8 h-8 bg-white/20 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5" />
              </div>
              <span className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk' }}>Immoverita Admin</span>
            </Link>
          </div>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/admin" className="text-sm opacity-80 hover:opacity-100">Übersicht</Link>
            <Link to="/admin/tenants" className="text-sm font-medium">Tenants</Link>
            <Link to="/admin/reviews" className="text-sm opacity-80 hover:opacity-100">Bewertungen</Link>
            <Link to="/admin/prompts" className="text-sm opacity-80 hover:opacity-100">Prompts</Link>
            <Link to="/admin/coupons" className="text-sm opacity-80 hover:opacity-100">Gutscheine</Link>
            <Link to="/admin/meta-scores" className="text-sm opacity-80 hover:opacity-100">MetaScores</Link>
          </nav>
          <Button variant="ghost" size="icon" onClick={logout} className="text-primary-foreground hover:bg-white/20">
            <LogOut className="w-4 h-4" />
          </Button>
        </div>
      </header>

      <main className="container-main py-8">
        {/* Breadcrumb */}
        <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/admin/tenants" className="hover:text-foreground flex items-center gap-1">
            <ArrowLeft className="w-4 h-4" />
            Tenants
          </Link>
          <span>/</span>
          <span className="text-foreground">{tenant.name}</span>
        </div>

        {/* Header */}
        <div className="flex items-start justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-primary" />
            </div>
            <div>
              <h1 className="text-2xl font-semibold">{tenant.name}</h1>
              <p className="text-muted-foreground">/{tenant.slug}</p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Status Card */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="text-lg">Status & Einstellungen</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-sm font-medium mb-2 block">Tenant Status</label>
                    <Select
                      value={tenant.status}
                      onValueChange={handleStatusChange}
                      disabled={updating}
                    >
                      <SelectTrigger data-testid="tenant-status-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Aktiv</SelectItem>
                        <SelectItem value="disabled">Deaktiviert</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Abo-Plan</label>
                    <Select
                      value={tenant.billing?.plan || 'free'}
                      onValueChange={handlePlanChange}
                      disabled={updating}
                    >
                      <SelectTrigger data-testid="tenant-plan-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">Free</SelectItem>
                        <SelectItem value="mvp">MVP</SelectItem>
                        <SelectItem value="standard">Standard</SelectItem>
                        <SelectItem value="premium">Premium</SelectItem>
                        <SelectItem value="enterprise">Enterprise</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium mb-2 block">Payment Status</label>
                    <Select
                      value={tenant.billing?.payment_status || 'trial'}
                      onValueChange={handlePaymentChange}
                      disabled={updating}
                    >
                      <SelectTrigger data-testid="tenant-payment-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="trial">Trial</SelectItem>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="past_due">Past Due</SelectItem>
                        <SelectItem value="canceled">Canceled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground">Bewertungen</p>
                  <p className="text-2xl font-bold">{review_count}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground">Links</p>
                  <p className="text-2xl font-bold">{link_count}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground">Plan</p>
                  <p className="text-2xl font-bold">{tenant.billing?.plan || 'MVP'}</p>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="pt-4">
                  <p className="text-sm text-muted-foreground">Erstellt</p>
                  <p className="text-lg font-bold">
                    {tenant.createdAt ? format(new Date(tenant.createdAt), 'dd.MM.yy', { locale: de }) : '-'}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* MetaScore Chart */}
            {meta_scores?.length > 0 && (
              <Card className="card-shadow">
                <CardHeader>
                  <CardTitle className="text-lg">MetaScore Verlauf</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={meta_scores}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} />
                        <XAxis
                          dataKey="captured_at"
                          tickFormatter={(val) => format(new Date(val), 'MMM yy', { locale: de })}
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis domain={[0, 5]} tick={{ fontSize: 12 }} />
                        <Tooltip
                          formatter={(val) => [`${val.toFixed(1)}/5`, 'Score']}
                          labelFormatter={(val) => format(new Date(val), 'dd.MM.yyyy', { locale: de })}
                        />
                        <Bar dataKey="value" fill="hsl(var(--accent))" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* HV User */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="text-lg">HV Benutzer</CardTitle>
              </CardHeader>
              <CardContent>
                {hv_user ? (
                  <div className="space-y-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Name</p>
                      <p className="font-medium">{hv_user.name || '-'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">E-Mail</p>
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-muted-foreground" />
                        <p className="font-medium">{hv_user.email}</p>
                      </div>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Letzter Login</p>
                      <p className="font-medium">
                        {hv_user.last_login_at
                          ? format(new Date(hv_user.last_login_at), 'dd.MM.yyyy HH:mm', { locale: de })
                          : 'Noch nie'
                        }
                      </p>
                    </div>
                    <Button
                      variant="secondary"
                      onClick={handleResetLogin}
                      className="w-full"
                      data-testid="tenant-reset-login-button"
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Neuen Login-Link senden
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="text-center py-4">
                      <UserPlus className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">Kein HV User vorhanden</p>
                    </div>
                    <Button
                      onClick={() => setCreateUserOpen(true)}
                      className="w-full"
                      data-testid="tenant-create-hv-user-button"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      HV User erstellen
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Public Profile */}
            <Card className="card-shadow">
              <CardHeader>
                <CardTitle className="text-lg">Public Profile</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-muted-foreground">Anzeigename</p>
                    <p className="font-medium">{tenant.public_profile?.display_name || tenant.name}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Bewertungslink</p>
                    <p className="text-xs break-all text-muted-foreground">
                      {window.location.origin}/r/{tenant.slug}/...
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Create HV User Dialog */}
        <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>HV Benutzer erstellen</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium mb-2 block">E-Mail-Adresse *</label>
                <Input
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser(prev => ({ ...prev, email: e.target.value }))}
                  placeholder="hausverwaltung@example.de"
                  data-testid="create-hv-user-email"
                />
              </div>
              <div>
                <label className="text-sm font-medium mb-2 block">Name (optional)</label>
                <Input
                  value={newUser.name}
                  onChange={(e) => setNewUser(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Max Mustermann"
                  data-testid="create-hv-user-name"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="secondary" onClick={() => setCreateUserOpen(false)}>Abbrechen</Button>
              <Button
                onClick={handleCreateUser}
                disabled={creatingUser || !newUser.email}
                data-testid="create-hv-user-submit"
              >
                {creatingUser && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
                Erstellen & Login-Link senden
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
